import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import emailService from '../../utils/emailService';
import authService from '../../utils/authService';
import { useAuth } from '../../hooks/useAuth';

// Components
// These components are used in the renderContent function
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // const { } = useAuth(); // Not needed for verification process
  
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Check for PKCE flow parameters (token_hash, type) - used by Supabase built-in emails
        const tokenHash = searchParams.get('token_hash');
        const type = searchParams.get('type');
        
        // Check for legacy custom parameters (token, email) - for backward compatibility
        const token = searchParams.get('token');
        const email = searchParams.get('email');
        
        console.log('🔍 Verification parameters:', { tokenHash, type, token, email });
        
        // Handle Supabase PKCE flow (recommended approach)
        if (tokenHash && type) {
          console.log('✅ Using Supabase PKCE flow (token_hash + type)');
          
          try {
            // Verify the OTP token hash using Supabase's verifyOtp method
            const { data, error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: type // 'signup', 'magiclink', 'recovery', etc.
            });

            if (error) {
              console.error('❌ Supabase verifyOtp error:', error);
              setVerificationStatus('error');
              setErrorMessage(error.message || 'Failed to verify email. The link may have expired.');
              return;
            }

            console.log('✅ Supabase verification successful:', data);

            // Get user information
            if (data?.user) {
              setUserEmail(data.user.email || '');
              
              // Fetch user profile for display
              const profileResult = await authService.getUserProfile(data.user.id);
              if (profileResult.data) {
                setUserRole(profileResult.data.role || '');
                setUserName(profileResult.data.full_name || '');
              }
            }

            setVerificationStatus('success');

            // Redirect to appropriate page after 3 seconds
            setTimeout(() => {
              navigate('/login-register?tab=login', { 
                state: { 
                  message: 'Email verified successfully! You can now log in.',
                  email: data?.user?.email,
                  justVerified: true
                }
              });
            }, 3000);
            return;
          } catch (error) {
            console.error('❌ PKCE verification error:', error);
            setVerificationStatus('error');
            setErrorMessage('An error occurred during verification. Please try again.');
            return;
          }
        }
        
        // Handle legacy custom verification flow (for backward compatibility)
        if (token && email) {
          console.log('⚠️ Using legacy custom verification flow (token + email)');
          
          setUserEmail(email);

          // Use the authService to verify the email
          console.log('Calling authService.verifyEmail with token:', token, 'and email:', email);
          const result = await authService.verifyEmail(token, email);
          console.log('Verification result:', result);
          
          const { success, data, error } = result;

          if (!success) {
            console.error('Email verification error:', error);
            setVerificationStatus('error');
            setErrorMessage(error || 'Failed to verify email. Please try again.');
            return;
          }

          // Set user information from the verification result
          console.log('Verification data:', data);
          if (data?.profile) {
            console.log('Profile data from verification:', data.profile);
            // Get full profile data to display user information
            const profileResult = await authService.getUserProfile(data.profile.id);
            console.log('getUserProfile result:', profileResult);
            
            if (profileResult.data) {
              console.log('Setting user info from profile:', profileResult.data);
              setUserRole(profileResult.data.role || '');
              setUserName(profileResult.data.full_name || '');
            }
          } else {
            console.log('No profile data in verification result');
          }
          
          setVerificationStatus('success');

          // Redirect to login page after 3 seconds
          setTimeout(() => {
            navigate('/login-register?tab=login', { 
              state: { 
                message: 'Email verified successfully! You can now log in.',
                email: email,
                justVerified: true
              }
            });
          }, 3000);
          return;
        }
        
        // No valid parameters found
        console.error('❌ Missing verification parameters');
        setVerificationStatus('error');
        setErrorMessage('Invalid verification link. Missing required parameters.');
        
      } catch (error) {
        console.error('❌ Verification process error:', error);
        setVerificationStatus('error');
        setErrorMessage('An unexpected error occurred during verification.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="text-center p-4">
            <div className="flex justify-center mb-4">
              <Icon name="Clock" size={60} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Verifying Your Email</h2>
            <LoadingSpinner />
            <p className="text-gray-600 mt-4">
              Please wait while we verify your email address...
            </p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center p-4">
            <div className="flex justify-center mb-4">
              <Icon name="CheckCircle" size={60} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Email Verified Successfully!</h2>
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              Your email has been verified and your account is now active.
            </div>
            <p className="mb-2">
              Welcome to VA Sprouts, {userName || 'valued member'}! Your {userRole} account is now ready to use.
            </p>
            <p className="text-gray-600 mb-4">
              You will be redirected to the login page in a few seconds...
            </p>
            <Link to="/login-register?tab=login" state={{ email: userEmail }}>
              <Button variant="primary">
                Go to Login
              </Button>
            </Link>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center p-4">
            <div className="flex justify-center mb-4">
              <Icon name="AlertCircle" size={60} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Verification Failed</h2>
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {errorMessage || 'There was a problem verifying your email.'}
            </div>
            <p className="mb-3">
              Please try the following:
            </p>
            <div className="text-left mb-4">
              <ul className="list-disc pl-6">
                <li>Check if you clicked the correct link from your email</li>
                <li>Try registering again with the same email</li>
                <li>Contact support if the problem persists</li>
              </ul>
            </div>
            <Link to="/login-register?tab=register">
              <Button variant="primary">
                Back to Registration
              </Button>
            </Link>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto max-w-md my-8 px-4">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        {renderContent()}
      </div>
    </div>
  );
};

export default VerifyEmail;


